import React, { useCallback, useState, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../lib/tenant-context';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Upload, FileText, CheckCircle2, AlertCircle, ChevronRight, ArrowLeft, Rocket, LayoutDashboard, UtensilsCrossed, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { parseCsv, validateCsv, structureMenu, importMenu } from '../../lib/csv-import';
import type { ValidationResult, StructuredMenu, ImportResult } from '../../lib/csv-import';
type Step = 'upload' | 'validate' | 'preview' | 'confirm' | 'success';
const STEPS: {
  key: Step;
  label: string;
}[] = [{
  key: 'upload',
  label: 'Subir'
}, {
  key: 'validate',
  label: 'Validar'
}, {
  key: 'preview',
  label: 'Vista previa'
}, {
  key: 'confirm',
  label: 'Confirmar'
}, {
  key: 'success',
  label: 'Listo'
}];
export function ImportPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [menu, setMenu] = useState<StructuredMenu | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // ─── File handling ─────────────────────────────────────────
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      alert('Solo se aceptan archivos .csv');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      setRawText(text);
      // Auto-advance to validation
      const rawRows = parseCsv(text);
      const validationResult = validateCsv(rawRows);
      setValidation(validationResult);
      setStep('validate');
    };
    reader.readAsText(file);
  }, []);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };
  // ─── Step transitions ──────────────────────────────────────
  const goToPreview = () => {
    if (!validation) return;
    const structured = structureMenu(validation.validRows);
    setMenu(structured);
    setStep('preview');
  };
  const goToConfirm = () => setStep('confirm');
  const handleImport = async () => {
    if (!menu || !restaurant) return;
    setImporting(true);
    const importResult = await importMenu(restaurant.id, menu);
    setResult(importResult);
    setImporting(false);
    if (importResult.success) {
      setStep('success');
    }
  };
  const reset = () => {
    setStep('upload');
    setFileName(null);
    setRawText(null);
    setValidation(null);
    setMenu(null);
    setResult(null);
  };
  // ─── Step index for progress ───────────────────────────────
  const currentStepIdx = STEPS.findIndex(s => s.key === step);
  // ─── Render ────────────────────────────────────────────────
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-2768">
      <div className="max-w-3xl mx-auto px-4 py-8" data-id="element-2769">
        {/* Header */}
        <div className="mb-8" data-id="element-2770">
          <button onClick={() => navigate(`/admin/${restaurant.slug}/menu`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4" data-id="element-2771">
            <ArrowLeft className="w-4 h-4" data-id="element-2772" />
            Volver al menú
          </button>
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2773">
            Importar menú desde CSV
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2774">
            Sube un archivo CSV con tu menú y lo crearemos automáticamente.
          </p>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1 mb-10" data-id="element-2775">
          {STEPS.map((s, idx) => <Fragment key={s.key} data-id="element-2776">
              <div className="flex items-center gap-2" data-id="element-2777">
                <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors', idx < currentStepIdx ? 'bg-green-500 text-white' : idx === currentStepIdx ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400')} data-id="element-2778">
                  {idx < currentStepIdx ? <CheckCircle2 className="w-4 h-4" data-id="element-2779" /> : idx + 1}
                </div>
                <span className={cn('text-xs font-medium hidden sm:block', idx <= currentStepIdx ? 'text-gray-900' : 'text-gray-400')} data-id="element-2780">
                  {s.label}
                </span>
              </div>
              {idx < STEPS.length - 1 && <div className={cn('flex-1 h-0.5 rounded-full mx-1', idx < currentStepIdx ? 'bg-green-500' : 'bg-gray-100')} data-id="element-2781" />}
            </Fragment>)}
        </div>

        {/* ═══ STEP 1: UPLOAD ═══ */}
        {step === 'upload' && <div onDragOver={e => {
        e.preventDefault();
        setDragOver(true);
      }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={cn('border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all', dragOver ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50')} data-id="element-2782">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" data-id="element-2783" />
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-2784">
              <Upload className="w-6 h-6 text-gray-400" data-id="element-2785" />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1" data-id="element-2786">
              Arrastra tu archivo CSV aquí
            </p>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2787">
              o haz clic para seleccionar
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg" data-id="element-2788">
              <FileText className="w-4 h-4" data-id="element-2789" />
              Seleccionar archivo
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-2790">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2791">
                Columnas requeridas
              </p>
              <div className="flex flex-wrap justify-center gap-2" data-id="element-2792">
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-2793">
                  category_name
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-2794">
                  item_name
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-2795">
                  price
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-2796">
                  station_code
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2" data-id="element-2797">
                Opcionales: description, option_name, option_price
              </p>
            </div>
          </div>}

        {/* ═══ STEP 2: VALIDATE ═══ */}
        {step === 'validate' && validation && <div className="space-y-6" data-id="element-2798">
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4" data-id="element-2799">
              <div className="bg-green-50 border border-green-200 rounded-xl p-5" data-id="element-2800">
                <div className="flex items-center gap-2 mb-1" data-id="element-2801">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-2802" />
                  <span className="text-sm font-semibold text-green-800" data-id="element-2803">
                    Filas válidas
                  </span>
                </div>
                <p className="text-3xl font-black text-green-700" data-id="element-2804">
                  {validation.validRows.length}
                </p>
              </div>
              <div className={cn('border rounded-xl p-5', validation.invalidRows.length > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200')} data-id="element-2805">
                <div className="flex items-center gap-2 mb-1" data-id="element-2806">
                  <AlertCircle className={cn('w-5 h-5', validation.invalidRows.length > 0 ? 'text-red-500' : 'text-gray-400')} data-id="element-2807" />
                  <span className={cn('text-sm font-semibold', validation.invalidRows.length > 0 ? 'text-red-800' : 'text-gray-600')} data-id="element-2808">
                    Filas con errores
                  </span>
                </div>
                <p className={cn('text-3xl font-black', validation.invalidRows.length > 0 ? 'text-red-600' : 'text-gray-400')} data-id="element-2809">
                  {validation.invalidRows.length}
                </p>
              </div>
            </div>

            {/* Invalid rows detail */}
            {validation.invalidRows.length > 0 && <div className="bg-white border border-red-200 rounded-xl overflow-hidden" data-id="element-2810">
                <div className="px-5 py-3 bg-red-50 border-b border-red-200" data-id="element-2811">
                  <p className="text-sm font-semibold text-red-800" data-id="element-2812">
                    Errores encontrados
                  </p>
                </div>
                <div className="max-h-60 overflow-y-auto divide-y divide-red-100" data-id="element-2813">
                  {validation.invalidRows.map(row => <div key={row.row} className="px-5 py-3" data-id="element-2814">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2815">
                        <span className="text-xs font-bold text-red-500 bg-red-100 px-2 py-0.5 rounded" data-id="element-2816">
                          Fila {row.row}
                        </span>
                      </div>
                      <ul className="space-y-0.5" data-id="element-2817">
                        {row.errors.map((err, i) => <li key={i} className="text-xs text-red-700 flex items-center gap-1" data-id="element-2818">
                            <span data-id="element-2819">•</span> {err}
                          </li>)}
                      </ul>
                    </div>)}
                </div>
              </div>}

            {/* File info */}
            {fileName && <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200" data-id="element-2820">
                <FileText className="w-5 h-5 text-gray-400" data-id="element-2821" />
                <span className="text-sm text-gray-700 font-medium flex-1 truncate" data-id="element-2822">
                  {fileName}
                </span>
                <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2823">
                  Cambiar archivo
                </button>
              </div>}

            {/* Actions */}
            <div className="flex gap-3" data-id="element-2824">
              <button onClick={reset} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2825">
                Volver
              </button>
              <button onClick={goToPreview} disabled={validation.validRows.length === 0} className={cn('flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold rounded-xl transition-colors', validation.validRows.length > 0 ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-2826">
                Continuar con {validation.validRows.length} filas
                <ChevronRight className="w-4 h-4" data-id="element-2827" />
              </button>
            </div>
          </div>}

        {/* ═══ STEP 3: PREVIEW ═══ */}
        {step === 'preview' && menu && <div className="space-y-6" data-id="element-2828">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3" data-id="element-2829">
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-2830">
                <p className="text-2xl font-black text-gray-900" data-id="element-2831">
                  {menu.totalCategories}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-2832">
                  Categorías
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-2833">
                <p className="text-2xl font-black text-gray-900" data-id="element-2834">
                  {menu.totalItems}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-2835">
                  Productos
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-2836">
                <p className="text-2xl font-black text-gray-900" data-id="element-2837">
                  {menu.totalOptions}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-2838">
                  Opciones
                </p>
              </div>
            </div>

            {/* Category breakdown */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-2839">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200" data-id="element-2840">
                <p className="text-sm font-semibold text-gray-900" data-id="element-2841">
                  Vista previa del menú
                </p>
              </div>
              <div className="divide-y divide-gray-100 max-h-[28rem] overflow-y-auto" data-id="element-2842">
                {menu.categories.map(cat => <div key={cat.name} className="px-5 py-4" data-id="element-2843">
                    <div className="flex items-center justify-between mb-3" data-id="element-2844">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-2845">
                        {cat.name}
                      </h3>
                      <span className="text-xs text-gray-400 font-medium" data-id="element-2846">
                        {cat.items.length}{' '}
                        {cat.items.length === 1 ? 'producto' : 'productos'}
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-2847">
                      {cat.items.map(item => <div key={`${cat.name}-${item.name}`} className="flex items-start justify-between gap-4 py-1.5" data-id="element-2848">
                          <div className="flex-1 min-w-0" data-id="element-2849">
                            <p className="text-sm text-gray-800 font-medium truncate" data-id="element-2850">
                              {item.name}
                            </p>
                            {item.description && <p className="text-xs text-gray-400 truncate" data-id="element-2851">
                                {item.description}
                              </p>}
                            {item.options.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-2852">
                                {item.options.map(opt => <span key={opt.name} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium" data-id="element-2853">
                                    {opt.name}
                                    {opt.price > 0 && ` +₡${opt.price}`}
                                  </span>)}
                              </div>}
                          </div>
                          <div className="text-right shrink-0" data-id="element-2854">
                            <p className="text-sm font-bold text-gray-900" data-id="element-2855">
                              ₡{item.price.toLocaleString()}
                            </p>
                            <p className="text-[10px] text-gray-400 font-mono" data-id="element-2856">
                              {item.station_code}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3" data-id="element-2857">
              <button onClick={() => setStep('validate')} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2858">
                Volver
              </button>
              <button onClick={goToConfirm} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" data-id="element-2859">
                Todo se ve bien
                <ChevronRight className="w-4 h-4" data-id="element-2860" />
              </button>
            </div>
          </div>}

        {/* ═══ STEP 4: CONFIRM ═══ */}
        {step === 'confirm' && menu && <div className="space-y-6" data-id="element-2861">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center" data-id="element-2862">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-2863">
                <span className="text-3xl" data-id="element-2864">🚀</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2865">
                ¿Crear el menú?
              </h2>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6" data-id="element-2866">
                Se crearán {menu.totalCategories} categorías, {menu.totalItems}{' '}
                productos
                {menu.totalOptions > 0 && ` y ${menu.totalOptions} opciones`} en{' '}
                <strong data-id="element-2867">{restaurant.name}</strong>.
              </p>

              {result && !result.success && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-left" data-id="element-2868">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2869">
                    <AlertCircle className="w-4 h-4 text-red-500" data-id="element-2870" />
                    <span className="text-sm font-semibold text-red-800" data-id="element-2871">
                      Error durante la importación
                    </span>
                  </div>
                  <p className="text-xs text-red-700" data-id="element-2872">{result.error}</p>
                  <p className="text-xs text-red-500 mt-1" data-id="element-2873">
                    Se crearon {result.categoriesCreated} categorías,{' '}
                    {result.itemsCreated} productos y {result.optionsCreated}{' '}
                    opciones antes del error.
                  </p>
                </div>}

              <div className="flex gap-3" data-id="element-2874">
                <button onClick={() => setStep('preview')} disabled={importing} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2875">
                  Volver
                </button>
                <button onClick={handleImport} disabled={importing} className={cn('flex-1 flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-bold rounded-xl transition-colors', importing ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-green-600 text-white hover:bg-green-700')} data-id="element-2876">
                  {importing ? <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-2877">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2878" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-2879" />
                      </svg>
                      Creando menú...
                    </> : <>
                      <Rocket className="w-4 h-4" data-id="element-2880" />
                      Crear menú
                    </>}
                </button>
              </div>
            </div>
          </div>}

        {/* ═══ STEP 5: SUCCESS ═══ */}
        {step === 'success' && result && <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center" data-id="element-2881">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2882">
              <span className="text-4xl" data-id="element-2883">🚀</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2884">
              ¡Tu menú está listo!
            </h2>
            <p className="text-sm text-gray-500 max-w-sm mx-auto mb-2" data-id="element-2885">
              Se crearon exitosamente:
            </p>
            <div className="flex justify-center gap-6 mb-8" data-id="element-2886">
              <div className="text-center" data-id="element-2887">
                <p className="text-2xl font-black text-green-600" data-id="element-2888">
                  {result.categoriesCreated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-2889">categorías</p>
              </div>
              <div className="text-center" data-id="element-2890">
                <p className="text-2xl font-black text-green-600" data-id="element-2891">
                  {result.itemsCreated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-2892">productos</p>
              </div>
              {result.optionsCreated > 0 && <div className="text-center" data-id="element-2893">
                  <p className="text-2xl font-black text-green-600" data-id="element-2894">
                    {result.optionsCreated}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2895">opciones</p>
                </div>}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto" data-id="element-2896">
              <button onClick={() => navigate(`/admin/${restaurant.slug}/menu`)} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" data-id="element-2897">
                <UtensilsCrossed className="w-4 h-4" data-id="element-2898" />
                Ver menú
              </button>
              <button onClick={() => navigate(`/admin/${restaurant.slug}/dashboard`)} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-2899">
                <LayoutDashboard className="w-4 h-4" data-id="element-2900" />
                Dashboard
              </button>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}